import { Grid, NumberInput, Skeleton, Stack } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';

import { FormSubsection } from '../../ui/FormSubsection.js';
import { DECIMAL_INPUT_PROPS } from '../../form/numberFieldPresets.js';
import { ValidatedNumberInput } from '../../form/validatedFields.js';
import { PreviousValueForField } from '../../form/PreviousValueLine.js';
import { RegisterFieldTab } from '../../form/RegisterFieldTab.js';
import { withUnit, unitSuffix } from '../../form/unitLabel.js';
import type {
  EnvironmentalCalculations,
  EnvironmentalDataFormData,
} from '../../../types/reports.js';

interface SteinbruecheSectionProps {
  form: UseFormReturnType<EnvironmentalDataFormData>;
  quarryCalc: EnvironmentalCalculations | null;
  disabled?: boolean;
  loading?: boolean;
}

// Rehabilitation categories with German labels
const REHABILITATION_CATEGORIES = [
  { key: 'rehabilitationNativeForest', label: 'Naturnaher Wald' },
  { key: 'rehabilitationGrassland', label: 'Grünland' },
  { key: 'rehabilitationWetlands', label: 'Feuchtgebiete' },
  { key: 'rehabilitationWaterBodies', label: 'Gewässer' },
  { key: 'rehabilitationStreams', label: 'Fließgewässer' },
  { key: 'rehabilitationLakesBiodiversity', label: 'Seen (Biodiversität)' },
  { key: 'rehabilitationLakesStorage', label: 'Seen (Wasserspeicher)' },
  { key: 'rehabilitationLakesRecreation', label: 'Seen (Erholung)' },
  { key: 'rehabilitationExtensiveAgriculture', label: 'Extensive Landwirtschaft' },
  { key: 'rehabilitationConventionalAgriculture', label: 'Konventionelle Landwirtschaft' },
  { key: 'rehabilitationCommercialForest', label: 'Wirtschaftswald' },
  { key: 'rehabilitationParkRecreation', label: 'Park/Erholung' },
  { key: 'rehabilitationCommercialIndustrial', label: 'Gewerbe/Industrie' },
  { key: 'rehabilitationOther', label: 'Sonstige' },
];

// rehabilitatedAreaTotal kommt ausschliesslich aus der Server-Antwort (quarryCalc), NIE aus einer
// eigenen Addition ueber form.getValues() -- dieselbe Regel wie bei den Rohstoffsummen
// (EinsatzstoffeSection). Aufbereitung identisch zu WasserSection: gesetzt und nicht Leerstring
// -> parseFloat, sonst Leerstring.
function rehabilitatedAreaTotalValue(value: string | null | undefined): number | '' {
  return value != null && value !== '' ? parseFloat(value) : '';
}

export function SteinbruecheSection({
  form,
  quarryCalc,
  disabled,
  loading,
}: SteinbruecheSectionProps) {
  if (loading) {
    return (
      <Grid>
        {Array.from({ length: 8 }, (_, i) => i).map((i) => (
          <Grid.Col span={{ base: 12, md: 6 }} key={i}>
            <Skeleton height={60} />
          </Grid.Col>
        ))}
      </Grid>
    );
  }

  return (
    <Stack>
      <FormSubsection title="Gesamtflächen">
        <Grid>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <ValidatedNumberInput
              form={form}
              field="quarryAreaTotal"
              kind="decimal"
              label="Gesamte Steinbruchfläche"
              description="Genehmigte Abbaufläche"
              disabled={disabled}
              unit="ha"
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <ValidatedNumberInput
              form={form}
              field="activeQuarryArea"
              kind="decimal"
              label="Aktive Steinbruchfläche"
              description="Derzeit in Nutzung"
              disabled={disabled}
              unit="ha"
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <ValidatedNumberInput
              form={form}
              field="temporaryHabitat"
              kind="decimal"
              label="Temporärer Lebensraum"
              description="Biodiversitätsfläche im aktiven Abbau"
              disabled={disabled}
              unit="ha"
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>
        </Grid>
      </FormSubsection>

      <FormSubsection title="Rekultivierung">
        <Grid>
          <Grid.Col span={{ base: 12, md: 4 }}>
            {/* Beim Anlegen der Meldung aus der Vorjahresmeldung vorbelegt (Backend-Initializer,
                Plan 32-04); im ersten Meldejahr leer und vom Melder einzugeben -- deshalb ein
                normales Eingabefeld und kein Sonderpfad im Frontend. */}
            <ValidatedNumberInput
              form={form}
              field="rehabilitatedAreaPriorYears"
              kind="decimal"
              label="Kumuliert bis Vorjahr"
              description="Rekultivierte Fläche bis Ende des Vorjahres"
              disabled={disabled}
              unit="ha"
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <ValidatedNumberInput
              form={form}
              field="rehabilitatedAreaCurrentYear"
              kind="decimal"
              label="Neu rekultiviert im Berichtsjahr"
              // Beugt der naheliegenden Fehllesung vor, hier gehoere der Gesamtbestand hin:
              // gefragt ist allein der Zugang. Ein Melder, der stattdessen die kumulierte Flaeche
              // eintraegt, zaehlt den Vorjahresstand ein zweites Mal -- die Summe rechts waere
              // still falsch, ohne dass eine Constraint anschlaegt.
              description="Nur der Zugang, nicht der Gesamtbestand"
              disabled={disabled}
              unit="ha"
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            {/* Wert kommt ausschliesslich aus quarryCalc (Server-Antwort), NIE aus einer eigenen
                Addition -- siehe rehabilitatedAreaTotalValue oben. */}
            {/* Erklaerung als `description` statt als LabelWithInfo-Tooltip: die beiden
                Nachbarfelder dieser Zeile tragen ebenfalls eine, und drei Felder nebeneinander
                stehen nur dann auf einer Linie, wenn alle drei denselben Aufbau haben (die
                `description` schiebt das Eingabefeld um 19 px nach unten). Die Klinkerlager-Zeile
                loest dasselbe andersherum -- dort traegt KEIN Feld eine Beschreibung, und das
                berechnete Feld behaelt seinen Tooltip. Massgeblich ist die Stimmigkeit innerhalb
                der Zeile, die der Blick als Einheit liest. */}
            <NumberInput
              label={withUnit('Kumuliert gesamt', 'ha')}
              description="Automatisch berechnet, nicht editierbar"
              value={rehabilitatedAreaTotalValue(quarryCalc?.rehabilitatedAreaTotal)}
              readOnly
              disabled
              suffix={unitSuffix('ha')}
              {...DECIMAL_INPUT_PROPS}
            />
            <RegisterFieldTab field="rehabilitatedAreaTotal" label="Rekultivierte Fläche: Kumuliert gesamt" />
            <PreviousValueForField field="rehabilitatedAreaTotal" />
          </Grid.Col>
        </Grid>
      </FormSubsection>

      <FormSubsection title="Rekultivierungskategorien">
        <Grid>
          {REHABILITATION_CATEGORIES.map((cat) => (
            <Grid.Col span={{ base: 12, md: 6, lg: 4 }} key={cat.key}>
              <ValidatedNumberInput
                form={form}
                field={cat.key}
                kind="decimal"
                label={cat.label}
                disabled={disabled}
                unit="ha"
                {...DECIMAL_INPUT_PROPS}
              />
            </Grid.Col>
          ))}
        </Grid>
      </FormSubsection>
    </Stack>
  );
}
